import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Plus } from "lucide-react";
import api, { fmtEUR } from "@/lib/api";
import { useCart } from "@/context/CartContext";

function Upsell() {
  const [items, setItems] = useState([]);
  const { add, setOpen } = useCart();
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setItems((r.data || []).slice(0, 2))).catch(() => {});
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="mt-10 max-w-2xl mx-auto text-left" data-testid="upsell-block">
      <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-green-hover)] mb-3">One more thing?</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-3 flex gap-3" data-testid={`upsell-${p.slug}`}>
            {p.images?.[0] && <img src={p.images[0]} alt="" className="w-16 h-16 rounded object-cover" />}
            <div className="flex-1">
              <div className="font-display text-sm font-medium">{p.name}</div>
              <div className="text-xs font-mono text-[color:var(--brunchi-green-hover)]">{fmtEUR(p.price_eur)}</div>
              <button onClick={() => { add(p, 1); setOpen(true); }}
                data-testid={`upsell-add-${p.slug}`}
                className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full pill-primary text-xs">
                <Plus size={12} /> Add
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let stopped = false;
    const poll = async () => {
      if (stopped || tries.current > 20) { if (tries.current > 20) setStatus("timeout"); return; }
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data); setStatus("paid"); clear();
          try {
            if (window.fbq) window.fbq("track", "Purchase", { value: 0, currency: "EUR", content_ids: [data.order_id] });
            if (window.gtag) window.gtag("event", "purchase", { transaction_id: data.order_number, currency: "EUR" });
          } catch {}
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {status === "polling" && (
        <>
          <Loader2 className="mx-auto animate-spin text-[color:var(--brunchi-terracotta)]" size={48} />
          <div className="font-serif text-2xl mt-4">Confirming your payment…</div>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-[color:var(--brunchi-success)]" size={64} />
          <h1 className="font-serif text-3xl lg:text-4xl mt-4">Thank you.</h1>
          <p className="mt-3 text-[color:var(--brunchi-ink-soft)]">
            Your order {order?.order_number && <strong className="font-mono">{order.order_number}</strong>} is being prepared with care.
            You will receive a confirmation email shortly.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/shop" data-testid="success-continue-btn" className="px-6 py-3 rounded-full pill-primary text-sm font-medium">Continue shopping</Link>
            <Link to="/account" className="px-6 py-3 rounded-full pill-outline text-sm font-medium">My orders</Link>
          </div>
          <Upsell />
        </>
      )}
      {(status === "failed" || status === "error" || status === "timeout") && (
        <>
          <XCircle className="mx-auto text-[color:var(--brunchi-error)]" size={64} />
          <div className="font-serif text-2xl mt-4">Payment not confirmed.</div>
          <p className="mt-3 text-[color:var(--brunchi-ink-soft)]">Please try checking out again or contact us.</p>
          <Link to="/shop" className="mt-6 inline-block px-6 py-3 rounded-full pill-outline text-sm">Back to shop</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-cancel-page">
      <XCircle className="mx-auto text-[color:var(--brunchi-ink-soft)]" size={64} />
      <div className="font-serif text-2xl mt-4">Payment cancelled.</div>
      <p className="mt-3 text-[color:var(--brunchi-ink-soft)]">Your basket is saved. Take your time.</p>
      <Link to="/checkout" className="mt-6 inline-block px-6 py-3 rounded-full pill-primary text-sm">Return to checkout</Link>
    </div>
  );
}
